import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { bandwidthAPI } from '../../services/api';
import { useBranchStore } from '../../store';
import usePermission from '../../hooks/usePermission';
import { downloadXlsx, exportRowsToExcel, fetchAllRows } from '../../utils/excel';
import DateTimeRange, { rangeParams } from '../../components/DateTimeRange/DateTimeRange';
import {
    speedText, bytesText, toKbps, toMbps, percentOf, MAX_MBPS, MIN_KBPS, KBPS_PER_MBPS, SOURCE_LABELS, CHANGE_LABELS, GATEWAY_LABELS,
} from '../../utils/bandwidthFormat';
import './BandwidthManager.css';

/**
 * Bandwidth Manager — every guest online now with the speed they have (their plan's,
 * the Dynamic Bandwidth Manager's, or one an admin set), how fast they are using it and
 * their data; each gateway's link and how full it is; every live speed change. An admin
 * changes a guest's speed live (the gateway gets it by CoA, no reconnect) or gives them
 * their plan's speed again, and sets up a gateway's Dynamic Bandwidth Manager.
 */

const LIVE_MS = 15000;
const STEP_KBPS = KBPS_PER_MBPS; // + / − change a guest's speed by 1 Mbps
const KEEP_OPTIONS = [
    { minutes: 0, label: 'Until I reset it' },
    { minutes: 30, label: '30 minutes' },
    { minutes: 60, label: '1 hour' },
    { minutes: 240, label: '4 hours' },
    { minutes: 1440, label: '24 hours' },
];

function fmt(date) {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', hour12: true });
}
// When a guest's time ends: "2 Nov, 8:42 pm".
function shortTime(date) {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
}
const clampKbps = (kbps) => Math.min(MAX_MBPS * KBPS_PER_MBPS, Math.max(MIN_KBPS * 4, Math.round(kbps)));

// embedded: inside Internet Plans (its Live Bandwidth view), under that page's own title.
export default function BandwidthManager({ embedded = false }) {
    const branchId = useBranchStore((s) => s.selectedBranch?._id);
    const tenantId = useBranchStore((s) => s.selectedTenant?._id);
    const { canEdit } = usePermission('sessions');
    const { canConfigure: canConfigureGateways } = usePermission('routers');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [live, setLive] = useState(true);
    const [search, setSearch] = useState('');
    const [editing, setEditing] = useState(null); // a session row: its speed form
    const [gatewayEditing, setGatewayEditing] = useState(null);
    const [busy, setBusy] = useState(null); // the session whose speed is being changed

    const scope = useCallback(() => {
        const p = { branchId, tenantId };
        Object.keys(p).forEach((k) => { if (!p[k]) delete p[k]; });
        return p;
    }, [branchId, tenantId]);

    const load = useCallback(async (quiet = false) => {
        if (!quiet) setLoading(true);
        try {
            const r = await bandwidthAPI.overview(scope());
            setData(r.data.data);
        } catch (err) {
            if (!quiet) {
                setData(null);
                toast.error(err.response?.data?.message || 'Could not load the bandwidth overview');
            }
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [scope]);

    useEffect(() => { load(); }, [load]);

    // Live: again every few seconds, quietly.
    const loadRef = useRef(load);
    loadRef.current = load;
    useEffect(() => {
        if (!live) return undefined;
        const t = setInterval(() => loadRef.current(true), LIVE_MS);
        return () => clearInterval(t);
    }, [live]);

    // The answer to a change, said plainly: applied live, or why not.
    const report = (res, done) => {
        if (res.data.success) toast.success(done);
        else toast.warn(res.data.message || 'The WiFi gateway did not take the change');
    };

    async function setSpeed(row, uploadKbps, downloadKbps, extra = {}) {
        setBusy(row._id);
        try {
            const res = await bandwidthAPI.setSpeed(row._id, { uploadKbps, downloadKbps, ...extra });
            report(res, `${guestName(row)}: now ↓ ${speedText(downloadKbps)} ↑ ${speedText(uploadKbps)}`);
            await load(true);
            return true;
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not change the speed');
            return false;
        } finally {
            setBusy(null);
        }
    }

    const step = (row, sign) => setSpeed(
        row,
        clampKbps(row.speed.uploadKbps + sign * STEP_KBPS),
        clampKbps(row.speed.downloadKbps + sign * STEP_KBPS),
        { reason: sign > 0 ? 'Increased by 1 Mbps' : 'Decreased by 1 Mbps' },
    );

    async function resetSpeed(row) {
        setBusy(row._id);
        try {
            const res = await bandwidthAPI.resetSpeed(row._id);
            report(res, `${guestName(row)}: back to the plan's speed`);
            await load(true);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not reset the speed');
        } finally {
            setBusy(null);
        }
    }

    const sessions = data?.sessions || [];
    const q = search.trim().toLowerCase();
    const shown = q
        ? sessions.filter((r) => [guestName(r), r.guest.mobile, r.guest.room, r.mac, r.ip, r.plan?.name, r.nas?.name]
            .some((v) => String(v || '').toLowerCase().includes(q)))
        : sessions;

    function exportSessions() {
        exportRowsToExcel(`bandwidth-${new Date().toISOString().slice(0, 10)}`, [
            { label: 'Guest', value: (r) => guestName(r) },
            { label: 'Mobile', value: (r) => r.guest.mobile },
            { label: 'Room', value: (r) => r.guest.room },
            { label: 'MAC', field: 'mac' },
            { label: 'IP', field: 'ip' },
            { label: 'Gateway', value: (r) => r.nas?.name },
            { label: 'Plan', value: (r) => r.plan?.name },
            { label: 'Speed control', value: (r) => (r.plan?.queueType === 'dynamic' ? 'Dynamic' : 'Fixed') },
            { label: 'Download now (Mbps)', value: (r) => toMbps(r.speed.downloadKbps) },
            { label: 'Upload now (Mbps)', value: (r) => toMbps(r.speed.uploadKbps) },
            { label: 'Speed set by', value: (r) => SOURCE_LABELS[r.speed.source] || r.speed.source },
            { label: 'Using ↓ (Mbps)', value: (r) => (r.throughput ? toMbps(r.throughput.downloadKbps) : '') },
            { label: 'Using ↑ (Mbps)', value: (r) => (r.throughput ? toMbps(r.throughput.uploadKbps) : '') },
            { label: 'Data used (MB)', value: (r) => Math.round((r.data.usedBytes || 0) / 1048576) },
            { label: 'Data limit (MB)', value: (r) => (r.data.quotaBytes ? Math.round(r.data.quotaBytes / 1048576) : 'Unlimited') },
            { label: 'Online since', field: 'startTime' },
            { label: 'Ends', field: 'endsAt' },
        ], shown, { sheetName: 'Guests online' });
    }

    return (
        <div className={`bw-wrap${embedded ? ' bw-embedded' : ''}`}>
            <div className="bw-header">
                <div>
                    {!embedded && <h1 className="bw-title">Bandwidth Manager</h1>}
                    <p className="bw-sub">
                        Each guest's speed now, how fast they are using it, and their data. Change a guest's speed live — the gateway
                        takes it at once (CoA), no reconnect. Guests on a <strong>dynamic</strong> plan share their gateway's link: each
                        between the plan's minimum and maximum, by what they use.
                    </p>
                </div>
                <div className="bw-actions">
                    <label className={`bw-live${live ? ' on' : ''}`}>
                        <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
                        <span className="bw-live-dot" aria-hidden="true"></span> Live
                    </label>
                    <button type="button" className="bw-btn" onClick={() => load()}><i className="fas fa-sync-alt" aria-hidden="true"></i> Refresh</button>
                    <button type="button" className="bw-btn" onClick={exportSessions} disabled={!shown.length}>
                        <i className="fas fa-file-excel" aria-hidden="true"></i> Export Excel
                    </button>
                </div>
            </div>

            {data && <Totals totals={data.totals} />}

            {data?.gateways?.length > 0 && (
                <section className="bw-section" aria-label="Gateways">
                    <h2 className="bw-section-title">Gateways</h2>
                    <div className="bw-gateways">
                        {data.gateways.map((g) => (
                            <Gateway key={g._id} gateway={g} onSettings={canConfigureGateways ? () => setGatewayEditing(g) : null} />
                        ))}
                    </div>
                </section>
            )}

            <section className="bw-section" aria-label="Guests online">
                <div className="bw-section-head">
                    <h2 className="bw-section-title">Guests online <span className="bw-count">{sessions.length}</span></h2>
                    <input className="bw-search" type="search" placeholder="Search guest, mobile, room, MAC, IP, plan…" value={search}
                        onChange={(e) => setSearch(e.target.value)} aria-label="Search guests" />
                </div>
                <div className="bw-table-wrap">
                    <table className="bw-table">
                        <thead>
                            <tr>
                                <th className="bw-sr">Sr. No.</th>
                                <th>Guest</th>
                                <th>Device</th>
                                <th>Plan</th>
                                <th>Speed now</th>
                                <th>Using now</th>
                                <th>Data</th>
                                <th>Ends</th>
                                {canEdit && <th>Change speed</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && !data && <tr><td colSpan={canEdit ? 9 : 8} className="bw-empty">Loading…</td></tr>}
                            {!loading && !shown.length && (
                                <tr><td colSpan={canEdit ? 9 : 8} className="bw-empty">{q ? 'No guest online matches the search' : 'No one is online right now'}</td></tr>
                            )}
                            {shown.map((r, i) => (
                                <SessionRow key={r._id} row={r} sr={i + 1} canEdit={canEdit} busy={busy === r._id}
                                    onStep={(sign) => step(r, sign)} onEdit={() => setEditing(r)} onReset={() => resetSpeed(r)} />
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            <ChangeLog scope={scope} refreshKey={data?.totals?.changes24h} />

            {editing && (
                <SpeedForm row={editing} onClose={() => setEditing(null)}
                    onSave={async (speed) => { if (await setSpeed(editing, speed.uploadKbps, speed.downloadKbps, speed.extra)) setEditing(null); }} />
            )}
            {gatewayEditing && (
                <GatewayForm gateway={gatewayEditing} onClose={() => setGatewayEditing(null)}
                    onSaved={() => { setGatewayEditing(null); load(true); }} />
            )}
        </div>
    );
}

const guestName = (r) => r.guest?.name || r.guest?.mobile || r.mac;

/** The figures in one strip. */
function Totals({ totals }) {
    const items = [
        { label: 'Guests online', value: totals.sessions },
        { label: 'Download now', value: speedText(totals.use?.downloadKbps) },
        { label: 'Upload now', value: speedText(totals.use?.uploadKbps) },
        { label: 'On a dynamic plan', value: totals.dynamicSessions },
        { label: 'Speed set by an admin', value: totals.pinnedSessions },
        { label: 'Speed changes (24 h)', value: totals.changes24h },
    ];
    return (
        <div className="bw-totals" role="group" aria-label="Figures">
            {items.map((item) => (
                <div key={item.label} className="bw-total">
                    <span className="bw-total-label">{item.label}</span>
                    <span className="bw-total-value">{item.value ?? 0}</span>
                </div>
            ))}
        </div>
    );
}

/** A gateway: its link, how full it is, the guests on it, its Dynamic Bandwidth Manager. */
function Gateway({ gateway: g, onSettings }) {
    const dyn = g.dynamicBandwidth || {};
    const meters = [
        { label: 'Download', used: g.use.downloadKbps, capacity: dyn.downloadCapacity },
        { label: 'Upload', used: g.use.uploadKbps, capacity: dyn.uploadCapacity },
    ];
    return (
        <article className="bw-gateway" aria-label={`Gateway ${g.name}`}>
            <header className="bw-gateway-head">
                <div>
                    <strong>{g.name}</strong>
                    <span className="bw-note">{g.sessions} online · {g.dynamicSessions} on a dynamic plan</span>
                </div>
                <span className={`bw-pill${dyn.enabled ? ' on' : ''}`}>{dyn.enabled ? 'Dynamic on' : 'Dynamic off'}</span>
            </header>
            {meters.map((m) => {
                const pct = percentOf(m.used, m.capacity);
                return (
                    <div key={m.label} className="bw-meter-row">
                        <span className="bw-meter-label">{m.label}</span>
                        <span className="bw-meter-text">
                            {speedText(m.used) === '—' ? '0' : speedText(m.used)}
                            {m.capacity ? ` of ${speedText(m.capacity)}` : ''}
                        </span>
                        {pct !== null && (
                            <span className="bw-meter" role="meter" aria-label={`${g.name} ${m.label.toLowerCase()} in use`} aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                                <span className={`bw-meter-fill${pct >= 90 ? ' full' : ''}`} style={{ width: `${pct}%` }}></span>
                            </span>
                        )}
                    </div>
                );
            })}
            {!g.coaEnabled && <p className="bw-warn">CoA is off on this gateway — speed changes apply only when a guest signs in again.</p>}
            {onSettings && (
                <button type="button" className="bw-btn bw-btn-small" onClick={onSettings}>
                    <i className="fas fa-sliders-h" aria-hidden="true"></i> Dynamic bandwidth settings
                </button>
            )}
        </article>
    );
}

function SessionRow({ row: r, sr, canEdit, busy, onStep, onEdit, onReset }) {
    const plan = r.plan;
    const dataText = r.data.quotaBytes
        ? `${bytesText(r.data.usedBytes)} of ${bytesText(r.data.quotaBytes)}`
        : `${bytesText(r.data.usedBytes)} · Unlimited`;
    const atMax = r.speed.downloadKbps >= MAX_MBPS * KBPS_PER_MBPS && r.speed.uploadKbps >= MAX_MBPS * KBPS_PER_MBPS;
    return (
        <tr>
            <td className="bw-sr">{sr}</td>
            <td>
                <strong>{guestName(r)}</strong>
                <div className="bw-note">{[r.guest.room && `Room ${r.guest.room}`, r.guest.name && r.guest.mobile].filter(Boolean).join(' · ')}</div>
            </td>
            <td>
                <span className="bw-mono">{r.mac}</span>
                <div className="bw-note">{[r.ip, r.nas?.name].filter(Boolean).join(' · ')}</div>
            </td>
            <td>
                {plan ? (
                    <>
                        {plan.name}
                        {plan.queueType === 'dynamic' && <span className="bw-tag">Dynamic</span>}
                        <div className="bw-note">
                            ↓ {plan.min.downloadKbps ? `${speedText(plan.min.downloadKbps)}–` : ''}{speedText(plan.max.downloadKbps)}
                            {' · '}↑ {plan.min.uploadKbps ? `${speedText(plan.min.uploadKbps)}–` : ''}{speedText(plan.max.uploadKbps)}
                        </div>
                    </>
                ) : <span className="bw-muted">—</span>}
            </td>
            <td>
                <span className="bw-speed">↓ {speedText(r.speed.downloadKbps)}</span>
                <span className="bw-speed">↑ {speedText(r.speed.uploadKbps)}</span>
                <div className="bw-note">
                    <span className={`bw-source bw-source-${r.speed.source}`}>{SOURCE_LABELS[r.speed.source] || r.speed.source}</span>
                    {r.speed.pinned && r.speed.pinnedUntil && <> until {new Date(r.speed.pinnedUntil).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}</>}
                </div>
            </td>
            <td className="bw-nowrap">
                {r.throughput
                    ? <>↓ {speedText(r.throughput.downloadKbps)}<div className="bw-note">↑ {speedText(r.throughput.uploadKbps)}</div></>
                    : <span className="bw-muted" title="Not reported by the gateway yet">—</span>}
            </td>
            <td className="bw-nowrap">
                {dataText}
                {r.data.quotaBytes ? <div className="bw-note">{bytesText(r.data.leftBytes)} left</div> : null}
            </td>
            <td className="bw-nowrap">{shortTime(r.endsAt)}</td>
            {canEdit && (
                <td>
                    <div className="bw-controls">
                        <button type="button" className="bw-icon-btn" onClick={() => onStep(-1)} disabled={busy} aria-label={`Slower: ${guestName(r)}`} title="1 Mbps slower">−</button>
                        <button type="button" className="bw-icon-btn" onClick={() => onStep(1)} disabled={busy || atMax} aria-label={`Faster: ${guestName(r)}`} title="1 Mbps faster">+</button>
                        <button type="button" className="bw-btn bw-btn-small" onClick={onEdit} disabled={busy}>Set speed</button>
                        {r.speed.source !== 'plan' && plan && (
                            <button type="button" className="bw-btn bw-btn-small" onClick={onReset} disabled={busy}>Plan speed</button>
                        )}
                    </div>
                </td>
            )}
        </tr>
    );
}

/** A guest's speed: download and upload, how long to keep it, why. */
function SpeedForm({ row, onClose, onSave }) {
    const [down, setDown] = useState(String(toMbps(row.speed.downloadKbps)));
    const [up, setUp] = useState(String(toMbps(row.speed.uploadKbps)));
    const [keep, setKeep] = useState(0);
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const downKbps = toKbps(down);
    const upKbps = toKbps(up);
    const invalid = [['Download', downKbps], ['Upload', upKbps]]
        .map(([label, kbps]) => (!Number.isFinite(kbps) || kbps < MIN_KBPS ? `${label}: at least ${MIN_KBPS} kbps`
            : kbps > MAX_MBPS * KBPS_PER_MBPS ? `${label}: at most ${MAX_MBPS} Mbps` : null))
        .find(Boolean);
    const nudge = (sign) => {
        setDown((v) => String(toMbps(clampKbps(toKbps(v || 0) + sign * STEP_KBPS))));
        setUp((v) => String(toMbps(clampKbps(toKbps(v || 0) + sign * STEP_KBPS))));
    };

    async function submit(e) {
        e.preventDefault();
        if (invalid) return;
        setSaving(true);
        await onSave({ uploadKbps: upKbps, downloadKbps: downKbps, extra: { pinMinutes: keep, ...(reason.trim() ? { reason: reason.trim() } : {}) } });
        setSaving(false);
    }

    return (
        <div className="bw-modal-overlay" onClick={onClose}>
            <form className="bw-modal" role="dialog" aria-label="Change speed" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
                <header className="bw-modal-head">
                    <h2>Change speed — {guestName(row)}</h2>
                    <button type="button" className="bw-close" onClick={onClose} aria-label="Close">×</button>
                </header>
                <p className="bw-note">
                    Now ↓ {speedText(row.speed.downloadKbps)} ↑ {speedText(row.speed.uploadKbps)} ({SOURCE_LABELS[row.speed.source]}).
                    {row.plan && ` Plan ${row.plan.name}: up to ↓ ${speedText(row.plan.max.downloadKbps)} ↑ ${speedText(row.plan.max.uploadKbps)}.`}
                    {' '}Up to {MAX_MBPS} Mbps each way.
                </p>
                <div className="bw-form-grid">
                    <label>Download (Mbps)
                        <input type="number" min="0.0625" max={MAX_MBPS} step="0.5" value={down} onChange={(e) => setDown(e.target.value)} required />
                    </label>
                    <label>Upload (Mbps)
                        <input type="number" min="0.0625" max={MAX_MBPS} step="0.5" value={up} onChange={(e) => setUp(e.target.value)} required />
                    </label>
                    <div className="bw-nudge">
                        <button type="button" className="bw-btn bw-btn-small" onClick={() => nudge(-1)}>− 1 Mbps</button>
                        <button type="button" className="bw-btn bw-btn-small" onClick={() => nudge(1)}>+ 1 Mbps</button>
                    </div>
                    <label>Keep this speed
                        <select value={keep} onChange={(e) => setKeep(Number(e.target.value))}>
                            {KEEP_OPTIONS.map((o) => <option key={o.minutes} value={o.minutes}>{o.label}</option>)}
                        </select>
                    </label>
                    <label className="bw-full">Reason (optional)
                        <input type="text" maxLength={200} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Conference streaming" />
                    </label>
                </div>
                {row.plan?.queueType === 'dynamic' && (
                    <p className="bw-note">The Dynamic Bandwidth Manager leaves this guest at the speed you set until it is reset (or the time above runs out).</p>
                )}
                {invalid && <p className="bw-error" role="alert">{invalid}</p>}
                <footer className="bw-modal-foot">
                    <button type="button" className="bw-btn" onClick={onClose}>Cancel</button>
                    <button type="submit" className="bw-btn bw-btn-primary" disabled={!!invalid || saving}>{saving ? 'Applying…' : 'Apply now'}</button>
                </footer>
            </form>
        </div>
    );
}

/** A gateway's Dynamic Bandwidth Manager: on or off, its link's speed, the reserve. */
function GatewayForm({ gateway, onClose, onSaved }) {
    const dyn = gateway.dynamicBandwidth || {};
    const [form, setForm] = useState({
        enabled: !!dyn.enabled,
        down: dyn.downloadCapacity ? String(toMbps(dyn.downloadCapacity)) : '',
        up: dyn.uploadCapacity ? String(toMbps(dyn.uploadCapacity)) : '',
        reserve: String(dyn.reservePercent ?? 10),
        interim: String(dyn.interimSeconds ?? 60),
    });
    const [saving, setSaving] = useState(false);
    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const res = await bandwidthAPI.updateGateway(gateway._id, {
                enabled: form.enabled,
                downloadCapacity: form.down ? toKbps(form.down) : 0,
                uploadCapacity: form.up ? toKbps(form.up) : 0,
                reservePercent: Number(form.reserve),
                interimSeconds: Number(form.interim),
            });
            toast.success(res.data.message || 'Saved');
            onSaved();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Could not save the settings');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="bw-modal-overlay" onClick={onClose}>
            <form className="bw-modal" role="dialog" aria-label="Dynamic bandwidth settings" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
                <header className="bw-modal-head">
                    <h2>Dynamic bandwidth — {gateway.name}</h2>
                    <button type="button" className="bw-close" onClick={onClose} aria-label="Close">×</button>
                </header>
                <p className="bw-note">
                    The gateway's internet link is shared out among its guests on a dynamic plan: everyone at their plan's maximum while
                    the link holds it; as it fills, each between their plan's minimum and maximum, by what they use. Checked every minute,
                    changed live by CoA.
                </p>
                <label className="bw-check">
                    <input type="checkbox" checked={form.enabled} onChange={set('enabled')} /> Turn on for this gateway
                </label>
                <div className="bw-form-grid">
                    <label>Internet download speed (Mbps)
                        <input type="number" min="0" step="1" value={form.down} onChange={set('down')} placeholder="e.g. 100" />
                    </label>
                    <label>Internet upload speed (Mbps)
                        <input type="number" min="0" step="1" value={form.up} onChange={set('up')} placeholder="e.g. 50" />
                    </label>
                    <label>Keep free (%)
                        <input type="number" min="0" max="50" step="1" value={form.reserve} onChange={set('reserve')} />
                    </label>
                    <label>Usage reported every (seconds)
                        <input type="number" min="30" max="600" step="10" value={form.interim} onChange={set('interim')} />
                    </label>
                </div>
                <p className="bw-note">Guests signing in after this report their use as often as set here; those already online, at their next sign-in.</p>
                <footer className="bw-modal-foot">
                    <button type="button" className="bw-btn" onClick={onClose}>Cancel</button>
                    <button type="submit" className="bw-btn bw-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                </footer>
            </form>
        </div>
    );
}

/** Every live speed change: by whom, from what to what, and whether the gateway took it. */
function ChangeLog({ scope, refreshKey }) {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [source, setSource] = useState('');
    const [range, setRange] = useState({ from: '', to: '' });
    const [exporting, setExporting] = useState(false);
    const limit = 25;

    const params = useCallback(() => {
        const p = { ...scope(), source, ...rangeParams(range) };
        Object.keys(p).forEach((k) => { if (!p[k]) delete p[k]; });
        return p;
    }, [scope, source, range]);

    useEffect(() => {
        let gone = false;
        bandwidthAPI.changes({ ...params(), page, limit })
            .then((r) => { if (!gone) { setRows(r.data.data || []); setTotal(r.data.total || 0); } })
            .catch(() => { if (!gone) setRows([]); });
        return () => { gone = true; };
    }, [params, page, refreshKey]);

    async function exportAll() {
        setExporting(true);
        try {
            const { rows: all, capped } = await fetchAllRows(async ({ page: p, limit: l }) => {
                const r = await bandwidthAPI.changes({ ...params(), page: p, limit: l });
                return { rows: r.data.data || [], total: r.data.total };
            }, { pageSize: 1000 });
            downloadXlsx(`speed-changes-${new Date().toISOString().slice(0, 10)}`, {
                sheetName: 'Speed changes',
                columns: [
                    { label: 'Time', field: 'createdAt' },
                    { label: 'Guest', field: 'username' },
                    { label: 'MAC', field: 'mac' },
                    { label: 'From ↓ (Mbps)', value: (c) => toMbps(c.from?.downloadKbps) },
                    { label: 'From ↑ (Mbps)', value: (c) => toMbps(c.from?.uploadKbps) },
                    { label: 'To ↓ (Mbps)', value: (c) => toMbps(c.to?.downloadKbps) },
                    { label: 'To ↑ (Mbps)', value: (c) => toMbps(c.to?.uploadKbps) },
                    { label: 'By', value: (c) => (c.source === 'admin' ? c.actorName || 'Admin' : CHANGE_LABELS[c.source]) },
                    { label: 'Gateway', value: (c) => GATEWAY_LABELS[c.gateway] || c.gateway },
                    { label: 'Reason', field: 'reason' },
                ],
                rows: all,
            });
            if (capped) toast.info('The first 10,000 changes were exported — narrow the dates for the rest.');
        } catch (_) {
            toast.error('Export failed');
        } finally {
            setExporting(false);
        }
    }

    const pages = Math.max(1, Math.ceil(total / limit));
    return (
        <section className="bw-section" aria-label="Speed changes">
            <div className="bw-section-head">
                <h2 className="bw-section-title">Speed changes <span className="bw-count">{total}</span></h2>
                <button type="button" className="bw-btn bw-btn-small" onClick={exportAll} disabled={exporting || !total}>
                    {exporting ? 'Exporting…' : 'Export Excel'}
                </button>
            </div>
            <div className="bw-filters">
                <select value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} aria-label="Changed by">
                    <option value="">Every change</option>
                    <option value="admin">By an admin</option>
                    <option value="dynamic">By the Dynamic Bandwidth Manager</option>
                    <option value="reset">Back to the plan</option>
                </select>
                <DateTimeRange value={range} onChange={(next) => { setRange(next); setPage(1); }} />
            </div>
            <div className="bw-table-wrap">
                <table className="bw-table">
                    <thead>
                        <tr>
                            <th className="bw-sr">Sr. No.</th>
                            <th>Time</th>
                            <th>Guest</th>
                            <th>From</th>
                            <th>To</th>
                            <th>By</th>
                            <th>Gateway</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!rows.length && <tr><td colSpan={7} className="bw-empty">No speed changes</td></tr>}
                        {rows.map((c, i) => (
                            <tr key={c._id}>
                                <td className="bw-sr">{(page - 1) * limit + i + 1}</td>
                                <td className="bw-nowrap">{fmt(c.createdAt)}</td>
                                <td>{c.username || '—'}<div className="bw-note bw-mono">{c.mac}</div></td>
                                <td className="bw-nowrap">↓ {speedText(c.from?.downloadKbps)} · ↑ {speedText(c.from?.uploadKbps)}</td>
                                <td className="bw-nowrap"><strong>↓ {speedText(c.to?.downloadKbps)} · ↑ {speedText(c.to?.uploadKbps)}</strong></td>
                                <td>
                                    {c.source === 'admin' ? c.actorName || 'Admin' : CHANGE_LABELS[c.source]}
                                    {c.reason && <div className="bw-note">{c.reason}</div>}
                                </td>
                                <td><span className={`bw-answer${c.gateway === 'ack' ? ' ok' : ''}`}>{GATEWAY_LABELS[c.gateway] || c.gateway || '—'}</span></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {total > limit && (
                <div className="bw-pagination">
                    <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
                    <span>Page {page} / {pages}</span>
                    <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
                </div>
            )}
        </section>
    );
}
